// -----------------------------------------------------------------------------
//  End‑to‑end wizard container – aligned with DRF serializer & Redux slice for doctor onboarding
// -----------------------------------------------------------------------------

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Stepper, Step, StepLabel, Button, Typography, Paper,
  useTheme, useMediaQuery, alpha, CircularProgress, Alert, Snackbar,
  LinearProgress, Tooltip, IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Save   as SaveIcon,
  Help   as HelpIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  fetchOnboardingData,
  fetchAvailableOptions,
  submitStep,
  saveDraft,
  resetStep,
} from '../../features/doctors/onboardingSlice';
import {
  validateBasicInfo,
  validateProfessionalDetails,
  validateDocuments,
  validatePracticeSetup,
  validatePaymentAndSubscription,
} from '../../utils/validation';
import BasicInformation       from './steps/BasicInformation';
import ProfessionalDetails    from './steps/ProfessionalDetails';
import DocumentVerification   from './steps/DocumentVerification';
import PracticeSetup          from './steps/PracticeSetup';
import PaymentAndSubscription from './steps/PaymentAndSubscription';
import { toast } from 'react-hot-toast';
import * as yup from 'yup';

// ─── UI helpers ──────────────────────────────────────────────────────────────
const steps = [
  'Basic Information',
  'Professional Details',
  'Document Verification',
  'Practice Setup',
  'Payment & Subscription',
];
const stepKeys = [
  'basic_info',
  'professional_details',
  'document_verification',
  'practice_setup',
  'payment_subscription',
];
const labelToKey = Object.fromEntries(steps.map((l, i) => [l, stepKeys[i]]));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0, height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));
const StyledStepper = styled(Stepper)(({ theme }) => ({
  '.MuiStepLabel-root .Mui-completed': { color: theme.palette.success.main },
  '.MuiStepLabel-root .Mui-active':   { color: theme.palette.primary.main  },
}));

// ─── Yup example (extend per‑step if you like) ───────────────────────────────
const validationSchemas = {
  0: yup.object().shape({
    fullName: yup.string().required('Full name is required'),
    email:    yup.string().email('Invalid email').required('Email is required'),
    phone:    yup.string().required('Phone is required'),
    gender:   yup.string().required('Gender is required'),
    dateOfBirth: yup.string().required('Date of birth is required'),
    address:  yup.string().required('Address is required'),
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
const DoctorOnboarding = () => {
  const theme          = useTheme();
  const isMobile       = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch       = useDispatch();
  const navigate       = useNavigate();

  const {
    currentStep,
    completedSteps = [],
    draftData      = {},
    loading,
    error,
  } = useSelector((s) => s.onboarding);

  // ─── local state -----------------------------------------------------------
  const [activeStep, setActiveStep] = useState(0);
  const [formData,   setFormData]   = useState({
    basicInfo:           {},
    professionalDetails: {},
    documents:           {},
    practiceSetup:       {},
    paymentSetup:        {},
    subscription:        {},
    professionalType:    '',
  });
  const [errors,    setErrors]    = useState({});
  const [snack,     setSnack]     = useState({ open:false, msg:'', sev:'success' });

  // ─── bootstrap -------------------------------------------------------------
  useEffect(() => {
    dispatch(fetchOnboardingData());
    dispatch(fetchAvailableOptions());
  }, [dispatch]);

  // set activeStep when backend tells us where we are
  useEffect(() => {
    if (currentStep) {
      const idx = stepKeys.indexOf(currentStep);
      if (idx !== -1) setActiveStep(idx);
    }
  }, [currentStep]);

  // hydrate formData from draftData
  useEffect(() => {
    if (!Object.keys(draftData).length) return;

    setFormData({
      basicInfo: {
        fullName:     draftData.basic_info?.full_name   || '',
        email:        draftData.basic_info?.email       || '',
        phone:        draftData.basic_info?.phone       || '',
        gender:       draftData.basic_info?.gender      || '',
        dateOfBirth:  draftData.basic_info?.date_of_birth || '',
        address:      draftData.basic_info?.address     || '',
      },
      professionalDetails: draftData.professional_details   || {},
      documents:           draftData.document_verification  || {},
      practiceSetup:       draftData.practice_setup         || {},
      paymentSetup:        draftData.payment_subscription   || {},
      subscription:        draftData.subscription           || {},
      professionalType:    draftData.professional_type      || '',
    });
  }, [draftData]);

  // ─── helpers ---------------------------------------------------------------
  const completedSet = useMemo(
    () => new Set(completedSteps), [completedSteps]
  );
  const progress = useMemo(
    () => (stepKeys.filter(k => completedSet.has(k)).length / stepKeys.length) * 100,
    [completedSet]
  );

  const handleChange = (section, data) => {
    setFormData((prev) => ({ ...prev, [section]: { ...prev[section], ...data } }));
  };

  // client‑side validation; fall back to server messages
  const validateStep = (stepIdx) => {
    switch (stepIdx) {
      case 0: return validateBasicInfo(formData.basicInfo);
      case 1: return validateProfessionalDetails(formData.professionalDetails);
      case 2: return validateDocuments(formData.documents, formData.professionalType);
      case 3: return validatePracticeSetup(formData.practiceSetup);
      case 4: return validatePaymentAndSubscription(formData.paymentSetup, formData.subscription);
      default: return {};
    }
  };

  // normalise keys for backend
  const transformForSubmit = (idx) => {
    const key = stepKeys[idx];
    switch (key) {
      case 'basic_info': {
        const b = formData.basicInfo;
        return {
          basic_info: {
            fullName:    b.fullName,
            email:       b.email?.toLowerCase().trim(),
            phone:       (b.phone || '').replace(/\D/g, ''),
            gender:      b.gender,
            dateOfBirth: b.dateOfBirth,
            address:     b.address,
          },
        };
      }
      case 'professional_details': return { professional_details: formData.professionalDetails };
      case 'document_verification':return { document_verification: formData.documents };
      case 'practice_setup':       return { practice_setup:       formData.practiceSetup };
      case 'payment_subscription': return { payment_subscription: formData.paymentSetup };
      default: return {};
    }
  };

  // ─── navigation ------------------------------------------------------------
  const next = async () => {
    const stepErrs = validateStep(activeStep);
    if (Object.keys(stepErrs).length) { setErrors(stepErrs); return; }

    try {
      const payload = transformForSubmit(activeStep);
      await dispatch(submitStep({ step: stepKeys[activeStep], data: payload })).unwrap();

      // auto‑save draft (best effort)
      dispatch(saveDraft({ ...formData, currentStep: stepKeys[activeStep] }));

      setActiveStep((s) => s + 1);
      setErrors({});
    } catch (e) {
      setSnack({ open:true, sev:'error', msg: e.message || 'Submission failed' });
    }
  };
  const back  = () => { setActiveStep((s) => s - 1); setErrors({}); };

  const handleResetStep = async () => {
    const key = stepKeys[activeStep];
    if (!completedSet.has(key)) {
      setSnack({ open:true, sev:'info', msg:'Step not completed yet' });
      return;
    }
    try {
      await dispatch(resetStep(key)).unwrap();
      setSnack({ open:true, sev:'success', msg:'Step reset' });
      setErrors({});
    } catch (e) {
      setSnack({ open:true, sev:'error', msg:e.message || 'Reset failed' });
    }
  };

  const saveProgress = async () => {
    try {
      await dispatch(saveDraft({ ...formData, currentStep: stepKeys[activeStep] })).unwrap();
      toast.success('Progress saved');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
  };

  // ─── render step panel -----------------------------------------------------
  const stepProps = { errors, onChange: handleChange };
  const renderStep = (idx) => {
    switch (idx) {
      case 0: return <BasicInformation       data={formData.basicInfo}           {...stepProps} section="basicInfo" />;
      case 1: return <ProfessionalDetails    formData={formData}                 {...stepProps} />;
      case 2: return <DocumentVerification   data={formData.documents} professionalType={formData.professionalType} {...stepProps} section="documents" />;
      case 3: return <PracticeSetup          data={formData.practiceSetup}       {...stepProps} section="practiceSetup" />;
      case 4: return <PaymentAndSubscription data={formData.paymentSetup} subscription={formData.subscription} {...stepProps} section="paymentSetup" />;
      default: return 'Unknown step';
    }
  };

  // ─── UI --------------------------------------------------------------------
  return (
    <Container maxWidth="md">
      <Box py={4}>
        <Typography variant="h4" align="center" fontWeight={700}
          sx={{
            mb:1,
            background:`linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent'
          }}>
          Complete Your Profile
        </Typography>
        <Typography align="center" color="text.secondary" mb={4}>
          Let’s get you set up to start practicing on PsychePoint
        </Typography>

        <StyledPaper>
          {/* progress bar */}
          <Box mb={4}>
            <LinearProgress variant="determinate" value={progress}
              sx={{
                height:8, borderRadius:4,
                backgroundColor: alpha(theme.palette.primary.main,0.1),
                '& .MuiLinearProgress-bar':{
                  borderRadius:4,
                  background:`linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                }
              }}/>
            <Typography variant="body2" color="text.secondary" align="center" mt={1}>
              {Math.round(progress)}% Complete
            </Typography>
          </Box>

          <StyledStepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical':'horizontal'}
          >
            {steps.map((label, i) => {
              const done = completedSet.has(stepKeys[i]);
              return (
                <Step key={label} completed={done}>
                  <StepLabel>
                    <Box display="flex" alignItems="center" gap={1}>
                      {label}
                      {done && <CheckCircleIcon color="success" fontSize="small"/>}
                      <Tooltip title={`Step ${i+1} of ${steps.length}`}>
                        <IconButton size="small"><HelpIcon fontSize="small"/></IconButton>
                      </Tooltip>
                    </Box>
                  </StepLabel>
                </Step>
              );
            })}
          </StyledStepper>

          <Box mt={4}>
            {error && (
              <Alert severity="error" sx={{ mb:3 }}>
                {typeof error === 'object' ? JSON.stringify(error) : error}
              </Alert>
            )}

            {renderStep(activeStep)}

            <Box display="flex" justifyContent="space-between" mt={4} gap={2}>
              <Button onClick={handleResetStep} startIcon={<SaveIcon/>} disabled={loading}>
                Reset Step
              </Button>
              <Box display="flex" gap={2}>
                {activeStep !== 0 && (
                  <Button onClick={back} disabled={loading}>
                    Back
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={next}
                  disabled={loading}
                  sx={{ background:`linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})` }}
                >
                  {loading
                    ? <CircularProgress size={24} color="inherit"/>
                    : activeStep === steps.length-1 ? 'Complete Setup' : 'Next'}
                </Button>
              </Box>
            </Box>
          </Box>
        </StyledPaper>

        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="outlined"
            onClick={saveProgress}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20}/> : <SaveIcon/>}
          >
            {loading ? 'Saving…' : 'Save Progress'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack(s => ({ ...s, open:false }))}
      >
        <Alert
          onClose={() => setSnack(s => ({ ...s, open:false }))}
          severity={snack.sev}
          sx={{ width:'100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DoctorOnboarding;
